/**
 * Payment attempt identity (B5).
 *
 * The Stripe idempotency key is scoped to ONE attempt at ONE order:
 *   cardpay:v1:<user>:<program>:<attemptId>
 *
 * A retry of the same attempt (network blip, double-tap, remount) must reuse the
 * SAME intent. A genuinely new order must NOT. This project has already been
 * bitten by the opposite mistake — a key so stable that a reorder replayed a
 * completed generation batch — so the attempt id is minted per order and
 * DISCARDED the moment an order is submitted.
 */

const KEY = (slug: string) => `cm.pay.attempt.${slug}`;

function uuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`.padEnd(36, "0");
}

/** Current attempt id for this programme, minting one if the order is new. */
export function currentPaymentAttempt(slug: string): string {
  try {
    const existing = localStorage.getItem(KEY(slug));
    if (existing) return existing;
    const next = uuid();
    localStorage.setItem(KEY(slug), next);
    return next;
  } catch {
    return uuid();
  }
}

/** Call after a submitted order (or an abandoned one) so the next order is new. */
export function resetPaymentAttempt(slug: string): void {
  try {
    localStorage.removeItem(KEY(slug));
  } catch {
    /* storage unavailable — a fresh id will be minted anyway */
  }
}
